import { useTheme } from "vuetify";
import { format } from "date-fns";
import {
  ActiveElement,
  ChartConfiguration,
  ChartData,
  ChartOptions,
  LinearScaleOptions,
  Scale,
  Tooltip,
  TooltipPosition,
} from "chart.js/auto";
import { useUtils } from "@/composables/useUtils";
import { Dictionary } from "@/types/Dictionary";
import { Timestamp } from "@/types/Timestamp";

const { isScaleLinearScaleOptions } = useUtils();

export function useChart() {
  const theme = useTheme();

  const gridColor: Dictionary<string> = {
    dark: "#404040",
    light: "#f0f0f0",
  };

  function tickCallback(value: string | number): number {
    return Number(Number(value).toPrecision(9));
  }

  function getChartOptions(
    minTime: Timestamp,
    maxTime: Timestamp,
    minVal?: number,
    maxVal?: number,
    pointRadius: number = 0,
  ): ChartOptions {
    return {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          axis: "x",
          backgroundColor: "#000",
          intersect: false,
          position: "fixed",
          xAlign: "right",
          yAlign: "center",
          callbacks: {
            title: function (contextItems) {
              return format(contextItems[0]?.parsed.x, "H:mm:ss.SSS");
            },
            label: function (contextItem) {
              return `${contextItem.dataset.label}: ${contextItem.parsed.y.toFixed(3)}`;
            },
          },
        },
      },
      responsive: true,
      animation: false,
      maintainAspectRatio: false,
      elements: {
        point: {
          radius: pointRadius,
        },
        line: {
          tension: 0.1,
        },
      },
      scales: {
        x: {
          type: "time",
          grid: {
            color: gridColor[theme.name.value],
            lineWidth: 1,
          },
          min: minTime,
          max: maxTime,
          time: {
            unit: "second",
            displayFormats: {
              second: "mm:ss",
              millisecond: "mm:ss.SSS",
            },
          },
          ticks: {
            autoSkip: true,
            minRotation: 20,
          },
        },
        y: {
          type: "linear",
          display: true,
          position: "right",
          min: minVal,
          max: maxVal,
          grid: {
            color: gridColor[theme.name.value],
            lineWidth: 1,
          },
          ticks: {
            callback: tickCallback,
          },
        },
        // y-axis-left is only used for drawing a vertical line on the left side
        // of the chart grid box. It does not correspond to any data series.
        "y-axis-left": {
          display: true,
          position: "left",
          min: 0,
          max: 0,
          grid: {
            lineWidth: 0,
          },
        },
      },
    };
  }

  function getChartConfig(
    options: ChartOptions,
    data: ChartData,
    beforeUpdate: (scale: Scale<LinearScaleOptions>) => void,
  ): ChartConfiguration {
    return {
      type: "line",
      data,
      options,
      plugins: [
        {
          id: "zoomLimiter",
          beforeUpdate(chart) {
            for (const scaleId of Object.keys(chart.scales).filter(
              (id) => id.charAt(0) === "y",
            )) {
              const scale = chart.scales[scaleId];
              if (isScaleLinearScaleOptions(scale)) {
                beforeUpdate(scale);
              }
            }
          },
          afterRender(chart) {
            for (const scaleId of Object.keys(chart.scales).filter(
              (id) => id.charAt(0) === "y",
            )) {
              const scale = chart.scales[scaleId];
              if (isScaleLinearScaleOptions(scale)) {
                delete scale.options.suggestedMin;
                delete scale.options.suggestedMax;
              }
            }
          },
        },
        {
          id: "intersectLine",
          afterRender(chart) {
            if (chart.tooltip) {
              const elements = chart.tooltip.getActiveElements();
              if (elements.length) {
                const x = elements[0].element.x;
                const yAxis = chart.scales.y;
                const ctx = chart.ctx;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, yAxis.top);
                ctx.lineTo(x, yAxis.bottom);
                ctx.lineWidth = 1;
                ctx.strokeStyle = "#aaa";
                ctx.setLineDash([4]);
                ctx.stroke();
                ctx.restore();
              }
            }
          },
        },
      ],
    };
  }

  Tooltip.positioners.fixed = (
    items: ActiveElement[],
  ): TooltipPosition | false => {
    if (!items.length) {
      return false;
    }
    const foundItem = items.find(
      (item) => item.element && item.element.hasValue(),
    );
    if (!foundItem) {
      return false;
    }
    const pos = foundItem.element.tooltipPosition(false);
    const tooltipWidth = 200; // how to obtain its actual width?
    const xAlign = pos.x < tooltipWidth ? "left" : "right";
    return {
      x: pos.x,
      y: 0,
      xAlign,
    };
  };

  return { getChartConfig, getChartOptions };
}
